/**
 * NX-5235 — Xero payroll journal, UI-side constants. The ten payroll figures a
 * journal is built from; sides are fixed by accounting, only the nominal code
 * is configurable. Mirrors nxsys-backend src/modules/xeroJournal/xero-journal.constants.ts.
 */
export type JournalLineKey =
  | 'GROSS_WAGES'
  | 'DIRECTORS_REMUNERATION'
  | 'EMPLOYER_NIC'
  | 'EMPLOYER_PENSION'
  | 'PAYE'
  | 'TOTAL_NICS'
  | 'STUDENT_PG_LOANS'
  | 'ATTACHMENT_ORDERS'
  | 'OTHER_DEDUCTIONS'
  | 'NET_WAGES'
  | 'TOTAL_PENSION';

export type NominalCodeEntry = {
  code: string;
  /** Free label shown next to the code; never exported to Xero. */
  description?: string;
};

export type NominalMapping = Partial<Record<JournalLineKey, NominalCodeEntry>>;

export const DEBIT_LINE_KEYS: JournalLineKey[] = [
  'GROSS_WAGES',
  'DIRECTORS_REMUNERATION',
  'EMPLOYER_NIC',
  'EMPLOYER_PENSION',
];

export const CREDIT_LINE_KEYS: JournalLineKey[] = [
  'PAYE',
  'TOTAL_NICS',
  'STUDENT_PG_LOANS',
  'ATTACHMENT_ORDERS',
  'OTHER_DEDUCTIONS',
  'NET_WAGES',
  'TOTAL_PENSION',
];

export const ALL_LINE_KEYS: JournalLineKey[] = [
  ...DEBIT_LINE_KEYS,
  ...CREDIT_LINE_KEYS,
];

export const JOURNAL_LINE_LABEL: Record<JournalLineKey, string> = {
  GROSS_WAGES: 'Gross wages',
  DIRECTORS_REMUNERATION: "Directors' remuneration",
  EMPLOYER_NIC: 'Employer NICs',
  EMPLOYER_PENSION: 'Employer pension contributions',
  PAYE: 'PAYE',
  TOTAL_NICS: 'Total NICs',
  STUDENT_PG_LOANS: 'Student/Postgraduate Loan deductions',
  ATTACHMENT_ORDERS: 'Attachment Order deductions',
  OTHER_DEDUCTIONS: 'Other deductions',
  NET_WAGES: 'Net wages',
  TOTAL_PENSION: 'Total pension contributions',
};

/**
 * What Nxsys puts into each account. Asked for in the 2026-09-16 huddle: Azam
 * wanted a line of text under every row so a user can see where a new payment
 * rate or deduction will land ("we need some kind of a text, what goes in this
 * 477 from Nxsys, what's included?"). Wording still to be confirmed by Azam/Jim.
 *
 * House style, so the two columns read consistently:
 *   Debits  — what the money was spent on, and where its matching debt lands.
 *   Credits — what makes up the figure, and who is owed it.
 *
 * Deliberately no account types (expense / liability / asset): the client's own
 * chart of accounts decides that, not us. BrightPay's KashFlow template maps
 * net wages to "1200 Bank account" — an asset — so any type we printed here
 * would be wrong for some employers. The Debits/Credits headings already carry
 * the side, which is the part we do know.
 */
export const JOURNAL_LINE_DESCRIPTION: Record<JournalLineKey, string> = {
  GROSS_WAGES:
    'What the work cost: net pay plus everything deducted from it — PAYE, employee NI, student and postgraduate loans, attachment orders, other deductions and employee pension. Any new payment rate is included automatically.',
  DIRECTORS_REMUNERATION:
    'The same figure as gross wages, for employees flagged as an NI director when the payroll ran. Omitted for periods with no directors.',
  EMPLOYER_NIC:
    "The employer's own National Insurance, on top of the employee's pay — the Payment Summary's NI Empr column. The debt for it sits in Total NICs.",
  EMPLOYER_PENSION:
    "The employer's own pension contributions including employer AVCs; a refund period shows as a negative. The debt for it sits in Total pension contributions.",
  PAYE: "Income tax deducted from employees — the Payment Summary's Tax Deducted column. Owed to HMRC, paid with the monthly P32.",
  TOTAL_NICS:
    'Employee NI plus the employer NI above — the whole National Insurance bill. Owed to HMRC, paid with the same P32.',
  STUDENT_PG_LOANS:
    'Student loan and postgraduate loan deductions. Owed to HMRC with the PAYE bill, who pass them to the Student Loans Company.',
  ATTACHMENT_ORDERS:
    'Attachment of earnings taken from employees, including the £1 admin fee the employer is allowed to keep. Only the debt portion is owed to the court or council named on the order.',
  OTHER_DEDUCTIONS:
    'Every deduction set up on the employee other than tax, NI, student loan and attachment orders — those have their own rows. Counts whether it comes off before or after tax, NI and pension. Some are owed onward (union dues, charity giving); others simply recover cash the employer already paid out (advances, salary loans), so there is nothing further to pay.',
  NET_WAGES:
    'Take-home pay — owed to the employee, and cleared when the bank file goes out.',
  TOTAL_PENSION:
    'Employee plus employer pension contributions including AVCs. Owed to the pension provider.',
};

export const MAX_CODE_LENGTH = 20;
export const MAX_DESCRIPTION_LENGTH = 100;

/** Output channels. API push is NX-5236 — kept out of the list until built. */
export const OUTPUT_OPTIONS = [{ label: 'Create Xero CSV File', value: 'CSV' }];

export const MODE_OPTIONS = [
  {
    label: 'Include individual records for each employee',
    value: 'INDIVIDUAL',
  },
  { label: 'Summary totals per pay date', value: 'SUMMARY' },
];

/** One preview row: Date · Reference · Nominal code · Debit · Credit. */
export type XeroJournalPreviewLine = {
  date: string;
  reference: string;
  accountCode: string;
  accountName?: string;
  debit?: number;
  credit?: number;
};

/** Keys with an empty code — the caller decides whether that matters. */
export const blankMappingKeys = (
  mapping: NominalMapping,
  keys: JournalLineKey[] = ALL_LINE_KEYS,
): JournalLineKey[] => keys.filter((k) => !mapping[k]?.code?.trim());

export const formatGbp = (value?: number): string =>
  value === undefined || value === null
    ? '–'
    : `£${value.toLocaleString('en-GB', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
